import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { COMPANY } from "@/config/site";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Services" },
  { to: "/calculateur", label: "Calculateur" },
  { to: "/flotte", label: "Flotte" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-slate-900/5 bg-white/80 backdrop-blur-xl">
      <a href="#contenu" className="skip-link">
        Aller au contenu principal
      </a>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigation principale">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s|à/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-slate-600 hover:bg-slate-100 hover:text-foreground"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:text-primary"
          >
            <Phone className="h-4 w-4" strokeWidth={2} />
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="header-devis-cta"
            className="rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#EA580C] hover:shadow-xl"
          >
            Devis gratuit
          </Link>
        </div>
        <button
          type="button"
          data-testid="mobile-menu-button"
          onClick={() => setOpen(true)}
          className="rounded-lg p-2 text-slate-700 hover:bg-slate-100 lg:hidden"
          aria-label="Ouvrir le menu"
          aria-expanded={open}
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

    </header>

    {/* Menu mobile — overlay plein écran (hors du header : le backdrop-filter
        du header créerait un bloc conteneur qui tronquerait le fixed) */}
    {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col brand-dark noise lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          data-testid="mobile-menu-overlay"
        >
          <div className="flex h-16 items-center justify-between px-4">
            <Logo dark />
            <button
              type="button"
              data-testid="mobile-menu-close"
              onClick={() => setOpen(false)}
              className="rounded-lg p-2 text-slate-300 hover:bg-white/10"
              aria-label="Fermer le menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Navigation mobile">
            {NAV.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${i}`}
                className={({ isActive }) =>
                  `border-b border-white/10 py-4 font-heading text-2xl font-semibold transition-colors duration-200 ${
                    isActive ? "text-accent" : "text-[#FAFAF7] hover:text-secondary"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-devis-cta"
              className="mt-8 rounded-full bg-accent px-6 py-4 text-center font-heading text-lg font-semibold text-white"
            >
              Devis gratuit sous 24 h
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="mobile-phone-link"
              className="mt-4 flex items-center justify-center gap-2 text-slate-300"
            >
              <Phone className="h-5 w-5" /> {COMPANY.phone}
            </a>
        </nav>
        </div>
      )}
    </>
  );
};

export default Header;
